import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { Formik, Form, FormikProps } from 'formik';

import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import GetAppIcon from '@material-ui/icons/GetApp';
import EditIcon from '@material-ui/icons/EditOutlined';
import Container from '@material-ui/core/Container';

import { formatAmountValue, fractionToPercent, isoDateTimeToLabel, formatNumber } from 'helpers/helpers';
import compose from 'helpers/compose';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import FormNumberField from 'components/FormFields/FormNumberField';
import FormSelectField from 'components/FormFields/FormSelectField';
import ControlButtons from 'components/ControlButtons';
import Background from 'components/Layout/Background';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
//import FreeSwitch from 'components/FormFields/FreeSwitch';
import Switch from 'components/FormFields/Switch';

import { RootStore } from 'stores/rootStore';
import { FeesOptions } from './Fees';
import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import useStyles from './styles';

import { RegularFeesData, FeesLimits } from 'pages/Accounts/Edit/Fees/Fees';
import { WorkflowRegularFeesStore } from 'stores/workflowStores/workflowRegularFeesStore';
import { WorkflowBasicStore } from 'stores/workflowStores/workflowBasicStore/workflowBasicStore';
//import Info from 'components/Tooltip/Info';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';
import { Flows } from 'pages/Accounts/Detail/enums';
import { Currency, AccountMode } from 'enums';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { outOfLimits, outOfLimitsText, f2p } from 'helpers/helpers';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface Props {
  rootStore: RootStore;
  tabChange?: Function;
  workflowBasicStore: WorkflowBasicStore;
  detailMode?: boolean;
  readOnly?: boolean;
  //flow?: Flows;
  accountCurrency?: Currency;
  detailStore?: AccountDetailStore;
  accountMode: AccountMode;
}

const RegularFees: React.FC<Props> = ({
  rootStore,
  tabChange,
  workflowBasicStore,
  detailMode,
  readOnly,
  //flow,
  accountCurrency,
  detailStore,
  accountMode,
}) => {
  //if (detailMode) readOnly = true; // EDS-4915
  const flow = detailStore?.flow?.id;
  const { accountId } = useParams<Params>();
  if (!accountMode && detailStore?.mode) accountMode = detailStore.mode;
  const [store] = useState(
    new WorkflowRegularFeesStore(rootStore, accountId, accountMode, detailMode ? ViewMode.DETAIL : undefined),
  );
  const { t } = useTranslation();
  const currency = detailMode ? accountCurrency! : rootStore.stores.editAccountStore.currency;

  const [editMode, setEditMode] = useState(!!detailMode && flow === Flows.PRICELIST && !readOnly);
  const classes = useStyles({ readOnly, editMode });
  const valueClass = `${classes.values} ${editMode ? classes.disabled : ''}`;
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [izFeeSavingsRuleAppliedInfo, setIzFeeSavingsRuleAppliedInfo] = useState(false);
  //const [indValues, setIndValues] = useState(false);

  useEffect(() => {
    rootStore.stores.editAccountStore.setStep(5);
  }, [rootStore.stores.editAccountStore]);

  useEffect(() => {
    (async () => {
      await store.fetchFees();
      if (store.draft) setEditMode(true);
    })();
  }, [store]);

  const downloadFees = useMemo(
    () => async () => {
      setPdfDownloading(true);
      await rootStore.stores.apiStore?.get(`/account/${accountId}/pdf/${detailMode ? 'change/' : ''}PRICE_LIST`);
      setPdfDownloading(false);
    },
    [accountId, rootStore.stores.apiStore, detailMode],
  );

  const resetServiceFees = (formikProps: FormikProps<RegularFeesData>) => {
    for (let i = 0, count = formikProps.values.service.length; i < count; i++) {
      const title = formikProps.values.service[i].title;
      formikProps.setFieldValue(`service[${i}].total`, f2p(store.data.options[title as keyof FeesOptions][0]));
    }
  };

  const zeroFee = (fee: number) => {
    return fee === 0 ? t('fees_feeIncluded') : formatAmountValue(fee, currency);
  };

  const controlButtons = (editMode: boolean, formikProps: FormikProps<RegularFeesData>, top?: boolean) => {
    return (
      <>
        {!editMode ? (
          <div className={classes.buttons}>
            <Button
              color="primary"
              variant="contained"
              id="edit-regular-fees-button"
              onClick={() => {
                setEditMode(!editMode);
                flow === Flows.PLAN && store.setDraft();
              }}
            >
              <EditIcon fontSize="medium" />
              {t('accountDetail_edit_PRICELIST')}
            </Button>
          </div>
        ) : (
          flow !== Flows.PLAN && (
            <div className={classes.buttons}>
              <Button
                id="reset-regular-fees-button"
                color="default"
                variant="contained"
                onClick={() => {
                  store.restoreOriginalData(formikProps);
                  setEditMode(!editMode);
                }}
              >
                {t('general_cancel')}
              </Button>
              <Button
                color="primary"
                id="submit-regular-fees-button"
                variant="contained"
                onClick={async () => {
                  const ok = await store.handleSubmit(formikProps.values);
                  if (ok) setEditMode(!editMode);
                }}
              >
                {t('general_ok')}
              </Button>
            </div>
          )
        )}
      </>
    );
  };

  const serviceFeeLower = (editMode: boolean, formikProps: FormikProps<RegularFeesData>) => {
    //const fee = formikProps.values.serviceLow?.total;
    return (
      <ListItem>
        <ListItemText
          className={classes.label}
          primary={t('fees_service_lower')}
          secondary={t('fees_service_lower_portfolio')}
        />

        {editMode ? (
          <>
            {formikProps.values.customValues ? (
              <FormNumberField
                fast={false}
                name={`serviceLow.total`}
                afterBlur={async () => {
                  store.recalculateSplit(
                    'serviceLow',
                    formikProps.values.serviceLow,
                    formikProps,
                    currency,
                    formikProps.values.performance.total,
                    false,
                    true,
                  );
                }}
                endAdornment="%"
                decimalScale={2}
                className={classes.input}
                error={outOfLimits(formikProps.values.serviceLow.total, store.feesLimits.LOW)}
                helperText={outOfLimitsText(formikProps.values.serviceLow.total, store.feesLimits.LOW)}
              />
            ) : (
              <FormSelectField
                fast={false}
                name="serviceLow.total"
                className={classes.input}
                afterChange={() =>
                  store.recalculateSplit(
                    'serviceLow',
                    formikProps.values.serviceLow,
                    formikProps,
                    currency,
                    formikProps.values.performance.total,
                    false,
                    false,
                  )
                }
              >
                {formikProps.values.options.LOW.map((feeOption) => (
                  <MenuItem key={`LOW${feeOption}`} value={f2p(feeOption)}>
                    {`${fractionToPercent(feeOption, 2)}`}
                  </MenuItem>
                ))}
              </FormSelectField>
            )}

            <ListItemText
              className={classes.info}
              secondary={`${fractionToPercent(formikProps.values.serviceLow?.ocp, 2, true)}${
                formikProps.values.serviceLow?.vat
                  ? ' + ' + fractionToPercent(formikProps.values.serviceLow?.vat, 2, true)
                  : ''
              } %`}
            />
          </>
        ) : (
          <>
            <ListItemText
              className={`${valueClass} ${classes.sum}`}
              primary={`${formatNumber(formikProps.values.serviceLow?.total, 2)} %`}
            />
            <ListItemText secondary="=" className={classes.plus} />
            <ListItemText
              className={classes.info}
              secondary={`${fractionToPercent(
                formikProps.values.serviceLow?.ocp + (formikProps.values.serviceLow?.vat || 0),
                2,
              )}
`}
            />
          </>
        )}

        <ListItemText secondary="+" className={classes.plus} />
        <ListItemText
          className={classes.info}
          secondary={`${fractionToPercent(formikProps.values.serviceLow?.iz, 2)}`}
        />
      </ListItem>
    );
  };

  const listHeader = () => {
    return (
      <>
        <ListItem>
          <ListItemText className={classes.label} primary={''} />
          <ListItemText className={classes.input} primary={''} />
          <ListItemText
            className={`${classes.info} ${classes.sum}`}
            secondary={`${t(`fees_OCP${accountMode === AccountMode.LTIP ? '_LTIP' : editMode ? '' : 'plusVat'}`)}`}
          />
          <ListItemText className={classes.plus} secondary={''} />
          <ListItemText className={`${classes.info} ${classes.sum}`} secondary={`${t('fees_IZ')}`} />
        </ListItem>
        <Divider />
      </>
    );
  };

  return (
    store.data && (
      <>
        <Formik initialValues={store.data} onSubmit={store.handleRefresh} enableReinitialize>
          {(formikProps) => (
            <FormWithoutEnterSubmit className={`${classes.root}${readOnly ? ' readOnly' : ''}`}>
              <Container maxWidth="md">
                <Background afterMenu={!flow}>
                  {store.data.validFrom && (
                    <Background inside>
                      <Typography variant="h3">
                        {t('accountDetail_fees_validFrom')}: {isoDateTimeToLabel(store.data.validFrom, 'd. M. yyyy')}
                      </Typography>
                    </Background>
                  )}

                  <Divider />

                  <Background inside>
                    <List className={classes.list}>
                      {listHeader()}
                      {(formikProps.values.performance?.total !== 0 || editMode) && (
                        <ListItem>
                          <ListItemText
                            className={classes.performanceLabel}
                            primary={t('fees_performance')}
                            secondary={t('fees_performanceInfo')}
                            /* //nf
                          secondary={
                            editMode
                              ? `(${t('general_max')} ${fractionToPercent(store.data.options.maxPerformance[0], 1)})`
                              : undefined
                          }
                          */
                          />

                          {!editMode ? (
                            <ListItemText
                              className={valueClass}
                              //primary={`${fractionToPercent(formikProps.values.performance?.total, 1)}`}
                              primary={`${formatNumber(formikProps.values.performance?.total, 1)} %`}
                            />
                          ) : (
                            <>
                              <FormNumberField
                                name="performance.total"
                                afterBlur={async () => {
                                  await formikProps.submitForm();
                                  if (store.modelOk) {
                                    resetServiceFees(formikProps);
                                    formikProps.submitForm();
                                  }
                                }}
                                endAdornment="%"
                                decimalScale={1}
                                error={
                                  formikProps.values.performance.total >
                                  Math.round(store.data.options.maxPerformance[0] * 10000) / 100
                                }
                                className={classes.input}
                              />
                              <ListItemText
                                className={classes.info}
                                secondary={`${fractionToPercent(formikProps.values.performance?.ocp, 2, true)}${
                                  formikProps.values.performance?.vat
                                    ? ' + ' + fractionToPercent(formikProps.values.performance?.vat, 2, true)
                                    : ''
                                } %`}
                              />
                              <ListItemText secondary="+" className={classes.plus} />
                              <ListItemText
                                className={classes.info}
                                secondary={`${fractionToPercent(formikProps.values.performance.iz, 2)}`}
                              />
                            </>
                          )}
                        </ListItem>
                      )}
                      <Divider />

                      {formikProps.values.service.map((fees, index) => (
                        <ListItem key={fees.title}>
                          <>
                            <ListItemText
                              primary={index === 0 ? t('fees_service') : ''}
                              secondary={index === 0 ? t('fees_service_portfolio') : ''}
                              className={classes.halfLabel}
                            />
                            <ListItemText
                              className={classes.secondary}
                              secondary={`${t('fees_' + fees.title + '_' + currency)}`}
                            />
                            {!editMode ? (
                              <>
                                <ListItemText
                                  className={`${valueClass} ${classes.sum}`}
                                  primary={`${formatNumber(fees.total, 2)} %`}
                                />
                                <ListItemText secondary="=" className={classes.plus} />
                                <ListItemText
                                  className={classes.info}
                                  secondary={`${fractionToPercent(fees.ocp + (fees.vat || 0), 2)}`}
                                />
                              </>
                            ) : (
                              <>
                                {formikProps.values.customValues ? (
                                  <FormNumberField
                                    fast={false}
                                    name={`service[${index}].total`}
                                    afterBlur={async () => {
                                      store.recalculateSplit(
                                        `service[${index}]`,
                                        formikProps.values.service[index],
                                        formikProps,
                                        currency,
                                        formikProps.values.performance.total,
                                        true,
                                        true,
                                      );
                                    }}
                                    endAdornment="%"
                                    decimalScale={2}
                                    className={classes.input}
                                    error={outOfLimits(
                                      formikProps.values.service[index].total,
                                      store.feesLimits[fees.title as keyof FeesLimits],
                                    )}
                                    helperText={outOfLimitsText(
                                      formikProps.values.service[index].total,
                                      store.feesLimits[fees.title as keyof FeesLimits],
                                    )}
                                  />
                                ) : (
                                  <FormSelectField
                                    fast={false}
                                    name={`service[${index}].total`}
                                    className={classes.input}
                                    afterChange={() =>
                                      store.recalculateSplit(
                                        `service[${index}]`,
                                        formikProps.values.service[index],
                                        formikProps,
                                        currency,
                                        formikProps.values.performance.total,
                                        true,
                                        false,
                                      )
                                    }
                                  >
                                    {formikProps.values.options[fees.title as keyof FeesOptions].map((fee) => (
                                      <MenuItem key={`${fees.title}${fee}`} value={f2p(fee)}>
                                        {`${fractionToPercent(fee, 2)}`}
                                      </MenuItem>
                                    ))}
                                  </FormSelectField>
                                )}
                                <ListItemText
                                  className={classes.info}
                                  secondary={`${fractionToPercent(fees.ocp, 2, true)}${
                                    fees.vat ? ' + ' + fractionToPercent(fees.vat, 2, true) : ''
                                  } %`}
                                />
                              </>
                            )}

                            <ListItemText secondary="+" className={classes.plus} />
                            <ListItemText className={classes.info} secondary={`${fractionToPercent(fees.iz, 2)}`} />
                          </>
                        </ListItem>
                      ))}
                      <Divider />
                      {serviceFeeLower(editMode, formikProps)}
                      {editMode && (
                        <>
                          <Divider />
                          <ListItem style={{ justifyContent: 'right', width: '65%' }}>
                            <Switch
                              name={`customValues`}
                              label={t('fees_indValues')}
                              //checked={indValues}
                              //afterChange={() => setIndValues(!indValues)}
                            />
                          </ListItem>
                        </>
                      )}
                    </List>
                    {editMode && (
                      <>
                        <Divider />
                        <div style={{ textAlign: 'center', paddingTop: '16px' }}>
                          <Button
                            color="default"
                            variant="contained"
                            onClick={() => {
                              if (store.data.templateExists) {
                                setSaveTemplateOpen(true);
                              } else {
                                store.handleSubmit(formikProps.values, true);
                              }
                            }}
                          >
                            {t('fees_saveTemplate')}
                          </Button>
                        </div>
                        <ConfirmDialog
                          noHeader
                          isOpen={saveTemplateOpen}
                          confirm={async () => {
                            const ok = await store.handleSubmit(formikProps.values, true);
                            if (ok) setSaveTemplateOpen(false);
                          }}
                          close={() => {
                            setSaveTemplateOpen(false);
                          }}
                        >
                          {t(`fees_saveTemplate_confirm`)}
                          {t(`fees_saveTemplate_confirm_${accountMode}`)}
                          {t(`fees_saveTemplate_confirm_${currency}`)}
                        </ConfirmDialog>
                      </>
                    )}
                    {!editMode && !detailMode && store.data.templateExists && (
                      <>
                        <Divider />
                        <div style={{ textAlign: 'center', paddingTop: '16px' }}>
                          <Button color="primary" variant="contained" onClick={store.useTemplate}>
                            {t('fees_useTemplate')}
                          </Button>
                        </div>
                      </>
                    )}
                  </Background>

                  <Divider />
                  <Typography variant="h3" className={`${classes.title} ${classes.sectionTitle}`}>
                    {t(`fees_additionalFees`)}
                  </Typography>

                  <Background inside>
                    <List className={classes.list}>
                      <ListItem>
                        <ListItemText className={classes.label2} primary={`${t('fees_rebalancing')}`} />
                        <ListItemText
                          className={valueClass}
                          primary={zeroFee(formikProps.values.common.rebalancingFee.value)}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText className={classes.label2} primary={`${t('fees_reallocation')}`} />
                        <ListItemText
                          className={valueClass}
                          primary={zeroFee(formikProps.values.common.reallocationFee.value)}
                        />
                      </ListItem>

                      {/*<ListItem>
                      <ListItemText className={classes.label2} primary={`${t('fees_custody')}`} />
                      <ListItemText
                        className={valueClass}
                        primary={zeroFee(formikProps.values.common.custodyFee.value)}
                      />
                    </ListItem>*/}
                      <ListItem>
                        <ListItemText className={classes.label2} primary={`${t('fees_accountManagement')}`} />
                        <ListItemText
                          className={valueClass}
                          primary={zeroFee(formikProps.values.common.accountManagementFee.value)}
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemText className={classes.label2} primary={`${t('fees_reporting')}`} />
                        <ListItemText
                          className={valueClass}
                          primary={zeroFee(formikProps.values.common.reportingFee.value)}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText className={classes.label2} primary={`${t('fees_ter')}`} />
                        <ListItemText
                          className={valueClass}
                          primary={`max. ${fractionToPercent(formikProps.values.common.averageTer, 2)}`}
                        />
                      </ListItem>

                      <ListItem>
                        <ListItemText className={classes.label2} primary={t('fees_transactional_fee_detail')} />
                      </ListItem>

                      <ListItem>
                        <ListItemText className={classes.label2} primary={`${t('fees_spotHedgingFee')}`} inset />
                        <ListItemText
                          className={valueClass}
                          primary={`max. ${fractionToPercent(formikProps.values.common.spotHedgingFee, 2)}`}
                        />
                      </ListItem>
                      {currency === Currency.CZK && (
                        <ListItem>
                          <ListItemText className={classes.label2} primary={`${t('fees_forexFee')}`} inset />
                          <ListItemText
                            className={valueClass}
                            primary={`max. ${fractionToPercent(formikProps.values.common.forexFee, 2)}`}
                          />
                        </ListItem>
                      )}

                      <Divider />
                      <ListItem>
                        <ListItemText className={classes.label2} primary={t('fees_includingVat')} />
                      </ListItem>
                    </List>
                  </Background>

                  {((!store.draft && flow === Flows.PLAN) || !detailMode) && controlButtons(editMode, formikProps)}
                </Background>
              </Container>

              <div className={classes.controlButtons}>
                <ControlButtons
                  isSubmitting={formikProps.isSubmitting}
                  onNext={async () => {
                    if (detailMode && editMode) await store.checkFeeSavings(formikProps.values);
                    store.data?.izFeeSavingsRuleApplied && !izFeeSavingsRuleAppliedInfo
                      ? setIzFeeSavingsRuleAppliedInfo(true)
                      : store.handleNext(formikProps.values, formikProps, flow, detailStore?.goFwd);
                  }}
                  nextText={t(detailMode && detailStore ? detailStore.nextStepLabel : 'accountStepper_contract')}
                  onBack={() => store.handleBack(tabChange, detailStore?.goBack)}
                  backText={t('fees_single_title')}
                  disabled={editMode && !detailMode}
                  hideBack={readOnly}
                  hideNext={readOnly}
                >
                  {!detailMode && (
                    <Button
                      variant="outlined"
                      type="button"
                      startIcon={pdfDownloading ? <CircularProgress size={20} /> : <GetAppIcon />}
                      onClick={async () => {
                        const ok = await store.handleSubmit(formikProps.values);
                        if (ok) downloadFees();
                      }}
                      disabled={editMode}
                    >
                      {`${t('general_download')} ${t('fees_PriceList')}`}
                    </Button>
                  )}
                </ControlButtons>
              </div>

              <ConfirmDialog
                centered
                noHeader
                isOpen={izFeeSavingsRuleAppliedInfo}
                confirm={() => store.handleNext(formikProps.values, formikProps, flow, detailStore?.goFwd)}
                close={() => setIzFeeSavingsRuleAppliedInfo(false)}
              >
                {t('accountPlan_izFeeSavingsRuleApplied_info')}
              </ConfirmDialog>
            </FormWithoutEnterSubmit>
          )}
        </Formik>
      </>
    )
  );
};

export default compose(inject('rootStore'), observer)(RegularFees);
